/**
 * A risk as a sentence in the console's language, composed from its kind and
 * the values the server interpolated, so no screen shows the server's English
 * title. Unknown kinds fall back to that title rather than dropping the risk —
 * a detector added after this build must still say something.
 *
 * Lifted out of the cockpit when a second screen needed the same sentences;
 * two copies of a kind→sentence table would drift on the first new detector.
 */

import type { useT } from '@/i18n/context'
import type { OverviewRisk } from '@/routes/fleet-telemetry'

export function riskSentence(r: OverviewRisk, t: ReturnType<typeof useT>): string {
  const p = r.params ?? {}
  switch (r.kind) {
    case 'host_offline':
      return t('live.risk.host_offline', { host: p.host ?? '' })
    case 'host_stale':
      return t('live.risk.host_stale', { host: p.host ?? '' })
    case 'node_contract_differs':
      return t('live.risk.node_contract_differs', { host: p.host ?? '' })
    case 'host_disk_high':
      return t('live.risk.host_disk_high', { host: p.host ?? '', percent: p.percent ?? '' })
    // `account_auth_required` is what api.RiskAccountAuth is. It was matched
    // here as `account_auth`, so the one risk that tells an operator to go and
    // sign in fell through to the server's English title on a Turkish console —
    // a case that never fails, only speaks the wrong language.
    case 'account_auth_required':
      return t('live.risk.account_auth', { account: p.account ?? '' })
    case 'account_exhausted':
      return t('live.risk.account_exhausted', { account: p.account ?? '' })
    case 'no_eligible_account':
      return t('live.risk.no_eligible_account')
    case 'dependency_down':
      return t('live.risk.dependency_down', { dependency: p.dependency ?? '' })
    // A person has to sign this off (ADR-0024). The server sends the approval's
    // own title, which is the only part of it worth a line here; the sentence
    // says what is being asked of the reader.
    //
    // These two come from the `risk.<kind>` family rather than `live.risk.*`.
    // That family was written for a risk list, has never been read by anything,
    // and still holds better sentences than the ones added beside it — including
    // a `.detail` per kind, which the tooltip on these rows currently fills with
    // the server's English. Consolidating the two families is worth doing and is
    // not this change; adding a *third* copy of a sentence that already existed
    // was the mistake worth not repeating.
    case 'approval_waiting':
      return t('risk.approval_waiting', { title: p.title ?? r.title })
    case 'approval_overdue':
      return t('risk.approval_overdue', { title: p.title ?? r.title })
    // Two different claims under one kind. A pane whose command has exited is
    // housekeeping the steward is already on its way to doing; a session still
    // running where the console cannot show it is the thing the detector exists
    // for. Only an explicit `live: "0"` takes the quieter sentence, so a server
    // that does not send the breakdown keeps the one it always said.
    case 'unlisted_sessions':
      if (p.live === '0') {
        return t('live.risk.unlisted_dead', {
          count: p.dead ?? p.count ?? '',
          sessions: p.sessions ?? '',
        })
      }
      return t('live.risk.unlisted_sessions', {
        count: p.count ?? '',
        sessions: p.sessions ?? '',
      })
    // Two labels, one subscription. The address is what tells the operator
    // *which* of the two is the lie, so it leads the sentence where the server
    // sent one; without it the sentence still names both and is still true.
    case 'duplicate_identity':
      return p.email
        ? t('live.risk.duplicate_identity', { accounts: p.accounts ?? '', email: p.email })
        : t('live.risk.duplicate_identity_plain', { accounts: p.accounts ?? '' })
    default:
      return r.title
  }
}

/**
 * How many of a risk's panes are finished and can be cleared away now.
 *
 * Here rather than at the call site because it is the same reading of the same
 * params as the sentence above, and a screen that offers the action while the
 * sentence says something else is worse than one that offers nothing. Zero for
 * every other kind, and for a server that sends no breakdown — an action
 * offered on a guess is an action that fails in front of the operator.
 */
export function clearableDeadPanes(r: OverviewRisk): number {
  if (r.kind !== 'unlisted_sessions') return 0
  const dead = Number(r.params?.dead)
  return Number.isFinite(dead) && dead > 0 ? dead : 0
}
